import React, { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  IconButton,
  Paper,
  Snackbar,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import type {
  NmapHost,
  NmapParseResult,
  NmapPort,
} from '../../utils/nmapXmlParser';
import {
  getHostDisplayName,
  getHostIp,
  toGnmapText,
  toHttpUrls,
  toNmapNormalText,
} from '../../utils/nmapXmlParser';

// ─── Helpers ──────────────────────────────────────────────────

const STATE_COLORS: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
  open: 'success',
  filtered: 'warning',
  closed: 'error',
  'open|filtered': 'warning',
  unfiltered: 'default',
};

function serviceString(port: NmapPort): string {
  if (!port.service) return '';
  const { product, version, extrainfo } = port.service;
  return [product, version, extrainfo].filter(Boolean).join(' ');
}

function portLabel(p: NmapPort): string {
  const svc = p.service?.name ? ` ${p.service.name}` : '';
  return `${p.portid}/${p.protocol}${svc}`;
}

// ─── Host Card ────────────────────────────────────────────────

const HostCard: React.FC<{ host: NmapHost; dimmed: boolean }> = ({ host, dimmed }) => {
  const theme = useTheme();
  const ip = getHostIp(host);
  const displayName = getHostDisplayName(host);

  const openPorts = host.ports.filter((p) => p.state === 'open');
  const filteredPorts = host.ports.filter((p) => p.state === 'filtered' || p.state === 'open|filtered');
  const closedPorts = host.ports.filter((p) => p.state === 'closed');
  const otherPorts = host.ports.filter(
    (p) => p.state !== 'open' && p.state !== 'filtered' && p.state !== 'open|filtered' && p.state !== 'closed',
  );

  const macAddr = host.addresses.find((a) => a.addrtype === 'mac');
  const codeBg = theme.palette.mode === 'dark' ? '#1a1f3c' : '#f5f7fa';

  return (
    <Accordion
      defaultExpanded={false}
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        '&:before': { display: 'none' },
        borderRadius: '8px !important',
        mb: 1.5,
        overflow: 'hidden',
        opacity: dimmed ? 0.45 : 1,
        transition: 'opacity 0.2s',
        '&:hover': { opacity: dimmed ? 0.7 : 1 },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2.5, py: 0.5 }}>
        <Box sx={{ width: '100%' }}>
          {/* Row 1: Host identity + status */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="subtitle1" sx={{ fontFamily: 'monospace', fontWeight: 700, mr: 0.5 }}>
              {displayName}
            </Typography>
            <Chip
              label={host.status === 'up' ? 'Up' : host.status}
              size="small"
              color={host.status === 'up' ? 'success' : 'error'}
              sx={{ fontWeight: 700, fontSize: '0.68rem', height: 20 }}
            />
            {host.osMatches.length > 0 && (
              <Chip
                label={host.osMatches[0].name}
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.68rem', height: 20 }}
              />
            )}
            {macAddr && (
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary, ml: 0.5 }}>
                {macAddr.addr}{macAddr.vendor ? ` (${macAddr.vendor})` : ''}
              </Typography>
            )}
          </Box>

          {/* Row 2: Open ports at a glance */}
          {openPorts.length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.75, mb: 0.25 }}>
              {openPorts.map((p, i) => (
                <Chip
                  key={`${p.portid}-${p.protocol}-${i}`}
                  label={portLabel(p)}
                  size="small"
                  color="success"
                  variant="outlined"
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: '0.72rem',
                    fontWeight: 600,
                    height: 22,
                    '& .MuiChip-label': { px: 0.8 },
                  }}
                />
              ))}
              {filteredPorts.length > 0 && (
                <Chip
                  label={`+${filteredPorts.length} filtered`}
                  size="small"
                  variant="outlined"
                  color="warning"
                  sx={{ fontSize: '0.68rem', height: 22 }}
                />
              )}
            </Box>
          )}

          {openPorts.length === 0 && host.status === 'up' && (
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, mt: 0.5, display: 'block' }}>
              Keine offenen Ports
              {filteredPorts.length > 0 && ` (${filteredPorts.length} filtered)`}
            </Typography>
          )}
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ px: 2.5, pb: 2.5 }}>
        {/* Meta info */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
          {host.uptime && (
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
              Uptime: {Math.round(parseInt(host.uptime.seconds, 10) / 86400)}d
              {host.uptime.lastboot ? ` (seit ${host.uptime.lastboot})` : ''}
            </Typography>
          )}
          {host.statusReason && (
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
              Reason: {host.statusReason}
            </Typography>
          )}
        </Box>

        {/* Extra ports summary */}
        {host.extraPorts.map((ep, i) => (
          <Typography key={i} variant="body2" sx={{ mb: 1, color: theme.palette.text.secondary, fontSize: '0.82rem' }}>
            {ep.count} Ports: <strong>{ep.state}</strong>
          </Typography>
        ))}

        {/* Open + filtered ports table */}
        {(openPorts.length > 0 || filteredPorts.length > 0 || otherPorts.length > 0) && (
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Port</TableCell>
                  <TableCell>State</TableCell>
                  <TableCell>Service</TableCell>
                  <TableCell>Version</TableCell>
                  <TableCell>Reason</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[...openPorts, ...filteredPorts, ...otherPorts].map((p, idx) => (
                  <TableRow key={`${ip}-${p.portid}-${p.protocol}-${idx}`}>
                    <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                      {p.portid}/{p.protocol}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={p.state}
                        size="small"
                        color={STATE_COLORS[p.state] ?? 'default'}
                        sx={{ fontWeight: 600, fontSize: '0.7rem', minWidth: 60 }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>{p.service?.name ?? ''}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.82rem' }}>
                      {serviceString(p)}
                    </TableCell>
                    <TableCell sx={{ color: theme.palette.text.secondary, fontSize: '0.82rem' }}>
                      {p.reason}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Closed ports collapsed */}
        {closedPorts.length > 0 && (
          <Accordion
            variant="outlined"
            sx={{ mb: 2, borderRadius: '6px !important', '&:before': { display: 'none' }, opacity: 0.5 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                {closedPorts.length} closed Port{closedPorts.length !== 1 ? 's' : ''}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    {closedPorts.map((p, idx) => (
                      <TableRow key={`closed-${p.portid}-${p.protocol}-${idx}`}>
                        <TableCell sx={{ fontFamily: 'monospace', color: theme.palette.text.secondary }}>
                          {p.portid}/{p.protocol}
                        </TableCell>
                        <TableCell>
                          <Chip label="closed" size="small" color="error" sx={{ fontSize: '0.65rem', height: 18, opacity: 0.6 }} />
                        </TableCell>
                        <TableCell sx={{ color: theme.palette.text.secondary }}>{p.service?.name ?? ''}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.78rem', color: theme.palette.text.secondary }}>
                          {serviceString(p)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Port scripts */}
        {host.ports
          .filter((p) => p.scripts.length > 0)
          .map((p) =>
            p.scripts.map((s) => (
              <Accordion
                key={`${ip}-${p.portid}-${s.id}`}
                variant="outlined"
                sx={{ mb: 1, borderRadius: '6px !important', '&:before': { display: 'none' } }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                    {p.portid}/{p.protocol} &mdash; {s.id}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box
                    component="pre"
                    sx={{
                      fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                      fontSize: '0.78rem',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-all',
                      m: 0,
                      p: 1.5,
                      borderRadius: 1,
                      bgcolor: codeBg,
                    }}
                  >
                    {s.output}
                  </Box>
                </AccordionDetails>
              </Accordion>
            )),
          )}

        {/* Host scripts */}
        {host.hostScripts.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: theme.palette.text.secondary, mb: 0.5, display: 'block' }}>
              Host Scripts
            </Typography>
            {host.hostScripts.map((s) => (
              <Accordion
                key={`hostscript-${s.id}`}
                variant="outlined"
                sx={{ mb: 1, borderRadius: '6px !important', '&:before': { display: 'none' } }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                    {s.id}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box
                    component="pre"
                    sx={{
                      fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                      fontSize: '0.78rem',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-all',
                      m: 0,
                      p: 1.5,
                      borderRadius: 1,
                      bgcolor: codeBg,
                    }}
                  >
                    {s.output}
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        )}

        {/* OS matches */}
        {host.osMatches.length > 1 && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: theme.palette.text.secondary, mb: 0.5, display: 'block' }}>
              OS Detection
            </Typography>
            {host.osMatches.map((om, i) => (
              <Typography key={i} variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.82rem' }}>
                {om.name} ({om.accuracy}%)
                {om.osclass.length > 0 && ` — ${om.osclass.map((c) => [c.vendor, c.osfamily, c.osgen].filter(Boolean).join(' ')).join(', ')}`}
              </Typography>
            ))}
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

// ─── Main Component ───────────────────────────────────────────

interface Props {
  result: NmapParseResult;
}

const NmapXmlResultView: React.FC<Props> = ({ result }) => {
  const theme = useTheme();
  const [tabIndex, setTabIndex] = useState(0);
  const [copied, setCopied] = useState(false);

  const gnmapText = toGnmapText(result);
  const nmapNormalText = toNmapNormalText(result);
  const httpUrls = toHttpUrls(result);
  const totalOpen = result.hosts.reduce((sum, h) => sum + h.ports.filter((p) => p.state === 'open').length, 0);
  const totalFiltered = result.hosts.reduce((sum, h) => sum + h.ports.filter((p) => p.state === 'filtered').length, 0);

  const sortedHosts = [...result.hosts].sort((a, b) => {
    const aOpen = a.ports.filter((p) => p.state === 'open').length;
    const bOpen = b.ports.filter((p) => p.state === 'open').length;
    if (aOpen > 0 && bOpen === 0) return -1;
    if (aOpen === 0 && bOpen > 0) return 1;
    return bOpen - aOpen;
  });

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard blocked */ }
  };

  return (
    <Box>
      {/* Warnings */}
      {result.warnings.map((w, i) => (
        <Alert key={i} severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
          {w}
        </Alert>
      ))}

      {/* Scan info header */}
      {result.scanInfo.args && (
        <Paper sx={{ p: 2, borderRadius: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: theme.palette.primary.main }}>
              Scan Info
            </Typography>
            {result.scanInfo.version && (
              <Chip label={`Nmap ${result.scanInfo.version}`} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
            )}
            {result.scanInfo.startstr && (
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                {result.scanInfo.startstr}
              </Typography>
            )}
            {result.runStats && (
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                Dauer: {result.runStats.elapsed}s
              </Typography>
            )}
          </Box>
          <Typography
            variant="body2"
            sx={{
              fontFamily: '"JetBrains Mono", "Fira Code", monospace',
              fontSize: '0.8rem',
              mt: 1,
              color: theme.palette.text.secondary,
              wordBreak: 'break-all',
            }}
          >
            {result.scanInfo.args}
          </Typography>
        </Paper>
      )}

      {/* Summary stats */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Chip
          label={`${result.hosts.length} Host${result.hosts.length !== 1 ? 's' : ''}`}
          color="primary"
          variant="outlined"
        />
        <Chip label={`${totalOpen} open`} color="success" variant="outlined" />
        {totalFiltered > 0 && (
          <Chip label={`${totalFiltered} filtered`} color="warning" variant="outlined" />
        )}
        {result.runStats && (
          <Chip
            label={result.runStats.exit === 'success' ? 'Scan abgeschlossen' : `Exit: ${result.runStats.exit}`}
            color={result.runStats.exit === 'success' ? 'success' : 'warning'}
            size="small"
          />
        )}
      </Box>

      {/* Tab toggle */}
      <Tabs
        value={tabIndex}
        onChange={(_, v) => setTabIndex(v)}
        sx={{ mb: 2 }}
      >
        <Tab label="Strukturierte Ansicht" />
        <Tab label="Nmap Output" />
        <Tab label="GNmap Text" />
        <Tab label={`URLs (${httpUrls.length})`} />
      </Tabs>

      {/* Structured view */}
      {tabIndex === 0 && (
        <Box>
          {sortedHosts.map((host, idx) => {
            const hasOpen = host.ports.some((p) => p.state === 'open');
            return (
              <HostCard
                key={`${getHostIp(host)}-${idx}`}
                host={host}
                dimmed={!hasOpen}
              />
            );
          })}
        </Box>
      )}

      {/* Classic nmap output view */}
      {tabIndex === 1 && (
        <Paper sx={{ p: 0, borderRadius: 2, position: 'relative' }}>
          <Tooltip title={copied ? 'Kopiert!' : 'In Zwischenablage kopieren'}>
            <IconButton
              onClick={() => handleCopy(nmapNormalText)}
              size="small"
              sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}
            >
              {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Box
            component="pre"
            sx={{
              fontFamily: '"JetBrains Mono", "Fira Code", monospace',
              fontSize: '0.82rem',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
              m: 0,
              p: 2.5,
              pr: 6,
              overflow: 'auto',
            }}
          >
            {nmapNormalText || 'Keine Daten'}
          </Box>
        </Paper>
      )}

      {/* GNmap text view */}
      {tabIndex === 2 && (
        <Paper sx={{ p: 0, borderRadius: 2, position: 'relative' }}>
          <Tooltip title={copied ? 'Kopiert!' : 'In Zwischenablage kopieren'}>
            <IconButton
              onClick={() => handleCopy(gnmapText)}
              size="small"
              sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}
            >
              {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Box
            component="pre"
            sx={{
              fontFamily: '"JetBrains Mono", "Fira Code", monospace',
              fontSize: '0.82rem',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
              m: 0,
              p: 2.5,
              pr: 6,
              overflow: 'auto',
            }}
          >
            {gnmapText || 'Keine Daten'}
          </Box>
        </Paper>
      )}

      {/* HTTP/HTTPS URL list */}
      {tabIndex === 3 && (
        <Paper sx={{ borderRadius: 2, position: 'relative' }}>
          <Tooltip title={copied ? 'Kopiert!' : 'Alle URLs kopieren'}>
            <IconButton
              onClick={() => handleCopy(httpUrls.join('\n'))}
              size="small"
              sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}
            >
              {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          {httpUrls.length === 0 ? (
            <Box sx={{ p: 2.5 }}>
              <Typography sx={{ color: theme.palette.text.secondary }}>
                Keine HTTP/HTTPS-Services gefunden.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ p: 2.5, pr: 6 }}>
              {httpUrls.map((url) => (
                <Box
                  key={url}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    py: 0.5,
                    '&:hover': { bgcolor: `${theme.palette.primary.main}08` },
                    borderRadius: 1,
                    px: 0.5,
                  }}
                >
                  <Typography
                    component="a"
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                      fontSize: '0.88rem',
                      color: theme.palette.primary.main,
                      textDecoration: 'none',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    {url}
                  </Typography>
                  <OpenInNewIcon sx={{ fontSize: 14, color: theme.palette.text.secondary, opacity: 0.5 }} />
                  <Tooltip title="URL kopieren">
                    <IconButton
                      size="small"
                      onClick={() => handleCopy(url)}
                      sx={{ ml: 'auto', opacity: 0.4, '&:hover': { opacity: 1 } }}
                    >
                      <ContentCopyIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              ))}
            </Box>
          )}
        </Paper>
      )}

      <Snackbar
        open={copied}
        autoHideDuration={2000}
        onClose={() => setCopied(false)}
        message="In Zwischenablage kopiert"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default NmapXmlResultView;
